import React, { useState } from 'react';
import './ArtemyStyles.css';
import './GovernanceStyles.css';

function PolicyCard({ name, desc, defaultOn, scolor, stxt }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <div className="policy-card">
      <div>
        <div className="pname">{name}</div>
        <div className="pdesc">{desc}</div>
        <div style={{ marginTop: '5px', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', color: scolor }}>{stxt}</div>
      </div>
      <button 
        className={`gov-toggle ${on ? 'on' : 'off'}`} 
        onClick={() => setOn(!on)}
      ></button>
    </div>
  );
}

function TRow({ cells }) {
  return (
    <tr>
      {cells.map((c, i) => (
        <td key={i} dangerouslySetInnerHTML={{ __html: c }}></td>
      ))}
    </tr>
  );
}

export default function PolicyEngine() {
  const pcrd = (name, desc, on, scolor, stxt) => (
    <PolicyCard key={name} name={name} desc={desc} defaultOn={on} scolor={scolor} stxt={stxt} />
  );

  return (
    <div className="fi">
      <div className="page-header">
        <div><div className="page-title">Policy & Enforcement Engine</div><div className="page-sub">Executable runtime control — every action evaluated, constrained, or blocked before execution</div></div>
        <div className="page-actions"><button className="artemy-btn artemy-btn-primary">+ New Policy</button></div>
      </div>

      <div style={{ background: 'rgba(255,77,106,.06)', border: '1px solid rgba(255,77,106,.18)', borderRadius: '12px', padding: '14px 18px', marginBottom: '14px', fontSize: '12.5px', color: 'var(--artemy-text-secondary)', lineHeight: 1.7 }} className="fi1">
        <strong style={{ color: 'var(--artemy-red)', fontFamily: 'var(--artemy-font-display)' }}>System of Control.</strong>
        Policy is not documentation — it is executable control logic applied at runtime. The Policy & Enforcement Engine defines, evaluates, and enforces all rules governing behavior across the delivery system. It sits between the Decisioning Engine and the Execution Layer: no action is executed unless it complies with policy. Enforcement is deterministic, sub-second, and non-bypassable.
      </div>

      <div className="g4 fi1" style={{ marginBottom: '18px' }}>
        <div className="mc"><div className="mc-name" style={{ marginBottom: '7px' }}>Active Policies</div><div className="mc-val">14</div></div>
        <div className="mc"><div className="mc-name" style={{ marginBottom: '7px' }}>Violations (7d)</div><div className="mc-val" style={{ color: 'var(--artemy-red)' }}>3</div></div>
        <div className="mc"><div className="mc-name" style={{ marginBottom: '7px' }}>Auto-enforced</div><div className="mc-val" style={{ color: 'var(--artemy-green)' }}>11</div></div>
        <div className="mc"><div className="mc-name" style={{ marginBottom: '7px' }}>Pending Review</div><div className="mc-val" style={{ color: 'var(--artemy-yellow)' }}>2</div></div>
      </div>

      <div className="g2 fi2" style={{ marginBottom: '14px' }}>
        <div className="gov-card" style={{ padding: '16px' }}>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '9.5px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '12px' }}>Enforcement Decision Model</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
            {[
              ['ALLOW','Action complies with all policies — proceeds autonomously','var(--artemy-green)','rgba(0,212,170,.08)'],
              ['CONSTRAIN','Action allowed with modified parameters or reduced scope','var(--artemy-accent)','rgba(61,127,255,.08)'],
              ['REQUIRE APPROVAL','Action requires human sign-off before execution','var(--artemy-yellow)','rgba(245,166,35,.08)'],
              ['ESCALATE','Action routed to higher authority for review','var(--artemy-purple)','rgba(167,139,250,.08)'],
              ['BLOCK','Action violates policy — execution prevented immediately','var(--artemy-red)','rgba(255,77,106,.08)'],
            ].map(([d,desc,c,bg]) => (
              <div key={d} style={{ background: bg, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', fontWeight: 600, color: c, width: '110px', flexShrink: 0 }}>{d}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--artemy-text-secondary)' }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="gov-card" style={{ padding: '16px' }}>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '9.5px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '12px' }}>Policy Evaluation Inputs</div>
          {[
            ['Action context','The specific action being requested, including scope and target'],
            ['Actor identity','Human user or agent identity with full attribute context'],
            ['Delivery Confidence','Current confidence score and trend at the relevant scope'],
            ['Risk signals','Real-time risk indicators from the Signal Processing Layer'],
            ['Security context','Auth state, permission scope, data classification, anomaly flags'],
            ['System state','Current environment, load, and blast radius assessment'],
            ['Policy hierarchy','Global → tenant → BU → team → service → environment'],
          ].map(([t,d]) => (
            <div key={t} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)', fontSize: '11.5px' }}>
              <span style={{ color: 'var(--artemy-accent)', fontFamily: 'var(--artemy-font-mono)', fontSize: '10.5px' }}>{t}</span>
              <span style={{ color: 'var(--artemy-text-secondary)' }}>{d}</span>
            </div>
          ))}
          <div style={{ marginTop: '10px', fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', color: 'var(--artemy-text-dim)' }}>All inputs evaluated simultaneously in &lt;50ms</div>
        </div>
      </div>

      <div className="fi2" style={{ marginBottom: '16px' }}>
        <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '10px' }}>Delivery Policies</div>
        {pcrd('Sprint Over-commitment Guard','Flag any sprint commitment exceeding team velocity by more than 15%',true,'var(--artemy-green)','Auto-enforced · Evaluates capacity, dependency risk, and historical accuracy')}
        {pcrd('Intent Drift Detection','Alert when story scope diverges more than 20% from parent PRD',true,'var(--artemy-green)','Auto-enforced · Triggers re-alignment workflow and confidence penalty')}
        {pcrd('Deployment Gate Enforcement','Block deployments below Delivery Confidence threshold of 75%',true,'var(--artemy-green)','Blocking Mode · Requires human approval to override')}
      </div>

      <div className="fi3" style={{ marginBottom: '16px' }}>
        <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '10px' }}>Security Policies</div>
        {pcrd('SOC2 Change Traceability','All production changes must link to an approved change record in the Forensics Ledger',true,'var(--artemy-green)','Auto-enforced · RBAC-controlled')}
        {pcrd('PII Data Path Monitoring','Alert when PRs modify data paths touching PII-classified fields',true,'var(--artemy-yellow)','Approval Mode · Awaiting policy owner review')}
        {pcrd('Vulnerability Scan Gate','PRs with CVSS > 7.0 blocked until remediated — no bypass permitted',true,'var(--artemy-green)','Blocking Mode · Integrated with dependency scanner')}
        {pcrd('Anomalous Access Detection','Block execution when agent access patterns deviate beyond 2σ from baseline',true,'var(--artemy-green)','Auto-enforced · Feeds Agent Confidence Engine')}
      </div>

      <div className="fi3" style={{ marginBottom: '16px' }}>
        <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '10px' }}>Agent Autonomy Policies</div>
        {pcrd('Agent Autonomy Ceiling — Level 1','No agent may perform write-back actions without human approval at current posture',true,'var(--artemy-green)','Locked (non-editable) · Level 2 unlocks at ≥90% agent confidence')}
        {pcrd('Blast Radius Limit','Agents cannot affect more than 3 services in a single action chain',true,'var(--artemy-green)','Auto-enforced · Dynamically adjusted based on risk context')}
        {pcrd('Privilege Escalation Prevention','Block any agent attempt to acquire permissions beyond initial scope assignment',true,'var(--artemy-green)','Blocking Mode · Immediate revocation on violation')}
        {pcrd('AI-generated Code Review Required','All AI-generated code must have human review before merge',false,'var(--artemy-text-dim)','Disabled · Pending team configuration')}
      </div>

      <div className="gov-card fi3" style={{ marginBottom: '14px' }}>
        <div className="card-header">
          <div className="card-title">⚡ Recent Policy Evaluations</div>
          <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', color: 'var(--artemy-text-dim)' }}>Last hour · {new Date().toLocaleTimeString()}</span>
        </div>
        <table className="dt">
          <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Policy Triggered</th><th>Decision</th><th>Latency</th></tr></thead>
          <tbody>
            <TRow cells={['2m ago','Astra Deploy Agent','Production deploy: payments-svc','Deployment Gate','<span class="pill pr">BLOCKED</span> — Conf: 71%','12ms']} />
            <TRow cells={['8m ago','J. Rivera (Eng)','PR merge: auth-refactor','SOC2 Traceability','<span class="pill pg">ALLOW</span>','8ms']} />
            <TRow cells={['14m ago','Astra Code Agent','Modify config: prod env','Blast Radius Limit','<span class="pill py">ESCALATE</span> — 4 services','15ms']} />
            <TRow cells={['22m ago','Astra Ticket Agent','Batch ticket update (17)','Agent Autonomy L1','<span class="pill pg">ALLOW</span>','6ms']} />
            <TRow cells={['45m ago','Pipeline Bot','Deploy to staging','Vulnerability Gate','<span class="pill pg">ALLOW</span> — CVSS: 3.2','9ms']} />
            <TRow cells={['1h ago','Astra Infra Agent','IAM role expansion attempt','Privilege Escalation','<span class="pill pr">BLOCKED</span> — Immediate revoke','7ms']} />
          </tbody>
        </table>
      </div>
      <div style={{ height: '40px' }}></div>
    </div>
  );
}
